import { useCallback, useEffect, useState, useMemo } from "react";
import {
  Button,
  Chip,
  Snackbar,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import genericStyles from "../../Styles/genericStyles.module.css";
import { Vocabulary } from "../../Utils/Vocabulary";
import { endpoints, localUrlEnum } from "../../Utils/UrlEnum";
import { EComponentTypes, EOnlineLessonTargetScope, OnlineLessonCompletedFilterOptions, OnlineLessonScopeOptions } from "../../Utils/Enums";
import { OnlineLessonModel } from "../../Utils/Models";
import { Filter, IOnlineLesson } from "../../Utils/Types";
import { getData } from "../../Services/getData";
import { deleteData } from "../../Services/deleteData";
import { updateData } from "../../Services/updateData";
import GenericModal from "../GenericModal";
import StandardActionButtons from "../StandarsActionButtons";
import OnlineLessonForm from "./OnlineLessonForm";
import { useNavigate, useLocation } from "react-router-dom";
import Config from "../../Utils/Config";
import moment from "moment";
import Filters from "../Widgets/Filters";
import * as yup from "yup";

interface LessonsState {
  lessons: IOnlineLesson[];
  count: number;
  page: number;
  perPage: number;
}

const SCOPE_LABELS: Record<string, string> = Object.fromEntries(
  OnlineLessonScopeOptions.map((o) => [o.value, o.name])
);

export default function OnlineLessonsTable() {
  const [state, setState] = useState<LessonsState>({
    lessons: [],
    count: 0,
    page: 0,
    perPage: Config.rowsPerPage,
  });
  const [openForm, setOpenForm] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState<OnlineLessonModel>(new OnlineLessonModel());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [snackbar, setSnackbar] = useState("");
  const [instructorNameById, setInstructorNameById] = useState<Record<string, string>>({});
  const [productNameById, setProductNameById] = useState<Record<string, string>>({});
  const [products, setProducts] = useState<any[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const isCompletedView = query.get("completed") === "true";

  const getOptionLabel = (option: any) => {
    if (!option) return "";
    if (typeof option === "object") return option?.name || "";
    return (
      OnlineLessonCompletedFilterOptions.find((o) => o.value === option)?.name ||
      OnlineLessonScopeOptions.find((o) => o.value === option)?.name ||
      option
    );
  };

  const currentTargetScope = new URLSearchParams(location.search).get("targetScope");

  const filters = useMemo<Filter[]>(() => [
    {
      property: "start",
      label: Vocabulary.startDate,
      type: EComponentTypes.DATE_PICKER,
      validation: yup.mixed().nullable(),
      defaultValue: null,
    },
    {
      property: "end",
      label: Vocabulary.endDate,
      type: EComponentTypes.DATE_PICKER,
      validation: yup.mixed().nullable(),
      defaultValue: null,
    },
    {
      property: "completed",
      label: Vocabulary.status,
      type: EComponentTypes.AUTOCOMPLETE,
      validation: yup.mixed().nullable(),
      defaultValue: null,
      options: OnlineLessonCompletedFilterOptions,
      autocompleteFilterProperty: "value",
      customGetOptionLabel: getOptionLabel,
    },
    {
      property: "targetScope",
      label: Vocabulary.onlineLessonTargetScope,
      type: EComponentTypes.AUTOCOMPLETE,
      validation: yup.mixed().nullable(),
      defaultValue: null,
      options: OnlineLessonScopeOptions,
      autocompleteFilterProperty: "value",
      customGetOptionLabel: getOptionLabel,
    },
    ...(currentTargetScope === EOnlineLessonTargetScope.SPECIFIC ? [{
      property: "productId",
      label: Vocabulary.products,
      type: EComponentTypes.AUTOCOMPLETE,
      validation: yup.mixed().nullable(),
      defaultValue: null,
      options: products,
      autocompleteFilterProperty: "_id",
    }] : []),
  ], [currentTargetScope, products, getOptionLabel]);

  const getInstructorDisplayName = (item: any) => {
    if (!item || typeof item !== "object") return "";

    const user = item.user_id || item;
    const firstName = user?.firstName || "";
    const lastName = user?.lastName || "";
    const fullName = `${lastName} ${firstName}`.trim();

    return fullName;
  };

  const fetchLessons = useCallback((page: number, perPage: number, params: URLSearchParams) => {
    let query = `?page=${page}&perPage=${perPage}`;
    if (params.get("completed") === "true") query += "&completed=true";
    const start = params.get("start");
    const end = params.get("end");
    const targetScope = params.get("targetScope");
    const productId = params.get("productId");
    if (start) query += `&start=${start}`;
    if (end) query += `&end=${end}`;
    if (targetScope) query += `&targetScope=${targetScope}`;
    if (productId) query += `&productId=${productId}`;
    getData(`${endpoints.onlineLessons.main}/paginated${query}`).then(
      (res: any) => {
        if (res?.data) {
          setState({
            lessons: res.data.results || [],
            count: res.data.numberOfResults || 0,
            page,
            perPage,
          });
        }
      }
    );
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const page = parseInt(params.get("page") || "0");
    const perPage = parseInt(params.get("perPage") || String(Config.rowsPerPage));
    fetchLessons(page, perPage, params);
  }, [location.search, fetchLessons]);

  const navigateWithParams = (page: number, perPage: number) => {
    const params = new URLSearchParams(location.search);
    params.set("page", String(page));
    params.set("perPage", String(perPage));
    navigate(`${localUrlEnum.onlineLessons}?${params.toString()}`);
  };

  useEffect(() => {
    getData(`${endpoints.instructor.user}/paginated?page=0&perPage=100000`).then(
      (res: any) => {
        const results = res?.data?.results || [];
        const map: Record<string, string> = {};

        results.forEach((item: any) => {
          const instructorId = item?.instructorDetails?.[0]?._id;
          const userId = item?._id;
          const name = getInstructorDisplayName(item);
          if (!name) return;

          if (instructorId) map[instructorId] = name;
          if (userId) map[userId] = name;
        });

        setInstructorNameById(map);
      }
    );

    getData(`${endpoints.product.main}/paginated?page=0&perPage=100000`).then(
      (res: any) => {
        const results = res?.data?.results || [];
        const map: Record<string, string> = {};

        results.forEach((product: any) => {
          if (product?._id && product?.name) {
            map[product._id] = product.name;
          }
        });

        setProductNameById(map);
        setProducts(results);
      }
    );
  }, []);

  const confirmDelete = () => {
    deleteData(`${endpoints.onlineLessons.main}/${selectedLesson._id}`).then(() => {
      setShowDeleteModal(false);
      fetchLessons(state.page, state.perPage, new URLSearchParams(location.search));
    });
  };

  const markLessonAsDone = (lessonId: string) => {
    updateData(`${endpoints.onlineLessons.main}/${lessonId}/complete`, {}).then(() => {
      fetchLessons(state.page, state.perPage, new URLSearchParams(location.search));
    });
  };

  const columns = [
    {
      name: "title",
      label: Vocabulary.onlineLessonTitle,
    },
    {
      name: "startDate",
      label: Vocabulary.onlineLessonScheduledAt,
      options: {
        sort: true,
        customBodyRender: (value: string) =>
          value ? moment(value).format(Config.momentEUDateTimeFormat) : "-",
      },
    },
    {
      name: "instructorId",
      label: Vocabulary.onlineLessonInstructor,
      options: {
        sort: false,
        customBodyRender: (instructor: any) => {
          if (!instructor) return "-";
          if (typeof instructor === "object") {
            const user = instructor.user_id || instructor;
            return `${user.lastName || ""} ${user.firstName || ""}`.trim() || "-";
          }
          return instructorNameById[instructor] || "-";
        },
      },
    },
    {
      name: "productIds",
      label: Vocabulary.products,
      options: {
        sort: false,
        customBodyRender: (products: any[]) => {
          if (!products?.length) return "-";
          return (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {products.map((p: any, i: number) => (
                <Chip
                  key={i}
                  label={typeof p === "object" ? p.name : productNameById[p] || "-"}
                  size="small"
                />
              ))}
            </div>
          );
        },
      },
    },
    {
      name: "targetScope",
      label: Vocabulary.onlineLessonTargetScope,
      options: {
        sort: false,
        customBodyRender: (scope: string) => SCOPE_LABELS[scope] || scope || "-",
      },
    },
    {
      name: "_id",
      label: " ",
      options: {
        sort: false,
        filter: false,
        setCellHeaderProps: () => ({ align: "right" }),
        setCellProps: () => ({ align: "right" }),
        customBodyRender: (_id: string, tableMeta: any) => {
          const lesson = state.lessons[tableMeta.rowIndex];
          if (!lesson) return null;
          return (
            <ToggleButtonGroup exclusive>
              {lesson.lessonUrl && (
                <Tooltip title={Vocabulary.joinOnlineLesson}>
                  <ToggleButton
                    value="join"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        localUrlEnum.onlineLessonRoom.replace(":id", lesson._id)
                      );
                    }}
                  >
                    <VideoCallIcon />
                  </ToggleButton>
                </Tooltip>
              )}
              <Tooltip title={Vocabulary.edit}>
                <ToggleButton
                  value="edit"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedLesson(lesson as any);
                    setOpenForm(true);
                  }}
                >
                  <EditIcon />
                </ToggleButton>
              </Tooltip>
              {!isCompletedView && (
                <Tooltip title={Vocabulary.finish}>
                  <ToggleButton
                    value="complete"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      markLessonAsDone(lesson._id);
                    }}
                  >
                    <CheckCircleOutlineIcon />
                  </ToggleButton>
                </Tooltip>
              )}
              <Tooltip title={Vocabulary.delete}>
                <ToggleButton
                  value="delete"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedLesson(lesson as any);
                    setShowDeleteModal(true);
                  }}
                >
                  <DeleteIcon />
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          );
        },
      },
    },
  ];

  const options: any = {
    selectableRows: "none",
    viewColumns: false,
    responsive: "standard",
    rowsPerPage: state.perPage,
    rowsPerPageOptions: Config.rowsPerPageOptions,
    filter: false,
    print: false,
    download: false,
    count: state.count,
    page: state.page,
    serverSide: true,
    textLabels: {
      body: { noMatch: Vocabulary.noResultsFound },
      pagination: {
        next: Vocabulary.nextPage,
        previous: Vocabulary.previousPage,
        rowsPerPage: Vocabulary.rowsPerPage,
        displayRows: Vocabulary.of,
      },
      toolbar: { search: Vocabulary.search },
      selectedRows: { text: Vocabulary.rowsSelected, delete: Vocabulary.delete },
    },
    onChangePage: (page: number) => {
      navigateWithParams(page, state.perPage);
    },
    onChangeRowsPerPage: (perPage: number) => {
      navigateWithParams(0, perPage);
    },
    customToolbar: () => (
      <div style={{ margin: 5 }}>
        <Button
          variant="contained"
          onClick={() => {
            setSelectedLesson(new OnlineLessonModel());
            setOpenForm(true);
          }}
        >
          <AddCircleIcon />
          {Vocabulary.addOnlineLesson}
        </Button>
      </div>
    ),
  };

  return (
    <div className={genericStyles.mainContainer}>
      <Filters filters={filters} defaultOpen={false} />
      <MUIDataTable
        title={Vocabulary.onlineLessonsPage}
        data={state.lessons}
        columns={columns}
        options={options}
      />

      <OnlineLessonForm
        lesson={selectedLesson}
        open={openForm}
        onClose={() => setOpenForm(false)}
        onSaved={(count) => {
          fetchLessons(state.page, state.perPage, new URLSearchParams(location.search));
          if (count && count > 1) {
            setSnackbar(`${count} ${Vocabulary.onlineLessonCreatedCount}`);
          }
        }}
      />

      <GenericModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={Vocabulary.confirmDeleteTitle}
        actions={
          <StandardActionButtons
            okAction={confirmDelete}
            cancelAction={() => setShowDeleteModal(false)}
            okText={Vocabulary.yes}
            cancelText={Vocabulary.no}
          />
        }
      >
        <div>{Vocabulary.confirmDeleteMessage}</div>
      </GenericModal>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar("")}
        message={snackbar}
      />
    </div>
  );
}
